// Local-player KO replay. When the local human goes from standing to KO'd,
// freeze the render-head at the KO tick and walk it forward at 0.25× real
// time (slowmo), then a single continuous "rampcatchup" sweep that runs
// 0.25× → peak (>1×) → 1× so the head lands exactly on live before we
// return to idle. The curve is smooth across both phase boundaries — no
// flat 1× plateau in the middle that used to read as a visible "pause"
// before the speed-up kicked in.
//
// `localPlayerId` is passed in (not hardcoded to 0) so the replay correctly
// tracks the local human's KO regardless of which sim slot the matchmaker
// assigned them in online play.

import { CONFIG } from '../config';
import type { SimState } from '../sim/SimState';

type Phase = 'idle' | 'slowmo' | 'rampcatchup';
// Halved from the previous 2.0s — the dramatic slow-mo got long fast once
// you'd seen it a few times.
const SLOWMO_S = 1.0;
const SLOWMO_RATE = 0.25;
// Duration of the rampcatchup sweep. Rate curve goes 0.25 → peak → 1.0 via
// two back-to-back smoothsteps (piecewise around τ=0.5). Peak is computed
// at phase entry so the integral over this window exactly closes the gap
// the slowmo built up. Audio has no pitch dependence on playbackRate (the
// low-pass cutoff is already clamped to bypass at ≥1×), so a peak of
// ~2.5–3× is purely a visual event — softened further by a brief blur the
// renderer overlays during this phase.
const RAMPCATCHUP_S = 1.0;

// Smoothstep helper (C¹ continuous, zero slope at 0 and 1).
function ss(a: number): number { return a * a * (3 - 2 * a); }

export class KoReplay {
  private phase: Phase = 'idle';
  private phaseElapsed = 0;
  // Float tick the renderer should display. Lives between oldestBufferedTick
  // and liveTick during an active replay; stays pinned to liveTick when idle.
  private headTickF = 0;
  // Edge trackers for the two discrete signals we care about.
  private prevKnockedOut = false;
  private prevRoundOver = false;
  // Instantaneous head velocity (ticks/real-sec) for the last step — used
  // to derive audio playback rate. Divided by tickHz = playback rate.
  private lastHeadVel = CONFIG.sim.tickHz;
  // Peak rate for the current rampcatchup. Computed at phase entry from the
  // actual tick gap so the piecewise-smoothstep rate curve integrates to
  // exactly close it over RAMPCATCHUP_S.
  private peakRate = 1;

  constructor(private localPlayerId: number = 0) {}

  /** Handle both edges off the live snap: kick off a replay on KO (idle
   *  → slowmo), and exit on got-up / round-reset (rampcatchup → idle).
   *  Call once per render frame with the current live snapshot, BEFORE step(). */
  armIfNeeded(liveSnap: SimState): void {
    const me = liveSnap.characters.find(c => c.id === this.localPlayerId);
    const ko = me?.knockedOut === true;
    const roundOver = liveSnap.roundOver;

    if (this.phase === 'idle' && ko && !this.prevKnockedOut) {
      this.phase = 'slowmo';
      this.phaseElapsed = 0;
      this.headTickF = liveSnap.tick;
      this.lastHeadVel = SLOWMO_RATE * CONFIG.sim.tickHz;
    } else if (this.phase === 'rampcatchup') {
      // Abort the sweep and snap to live if the player stood back up
      // (they'd rather see themselves live than watch the tail of an old
      // replay) or the round was reset. Round-reset is also handled from
      // FxBus via an explicit reset() on the roundOver→playing edge, but
      // this keeps the in-class state machine correct either way.
      const gotUp = this.prevKnockedOut && !ko;
      const roundReset = this.prevRoundOver && !roundOver;
      if (gotUp || roundReset) {
        this.headTickF = liveSnap.tick;
        this.phase = 'idle';
        this.phaseElapsed = 0;
      }
    }

    this.prevKnockedOut = ko;
    this.prevRoundOver = roundOver;
  }

  /** Advance the replay clock. Must be called once per render frame even
   *  when idle so lastHeadVel stays synchronized for audioRate. */
  step(realDt: number, liveTick: number): void {
    if (this.phase === 'idle') {
      this.headTickF = liveTick;
      this.lastHeadVel = CONFIG.sim.tickHz;
      return;
    }

    const prevHead = this.headTickF;
    const tickHz = CONFIG.sim.tickHz;

    if (this.phase === 'slowmo') {
      this.phaseElapsed += realDt;
      this.headTickF += realDt * SLOWMO_RATE * tickHz;
      if (this.phaseElapsed >= SLOWMO_S) {
        // Enter rampcatchup. Measure the remaining tick gap and solve for
        // the peak of a piecewise-smoothstep rate curve 0.25 → peak → 1.0.
        // Integral over the unit interval works out to 0.3125 + 0.5·peak
        // (see derivation in rampRate), so we pick peak such that
        // RAMPCATCHUP_S·(0.3125 + 0.5·peak) = RAMPCATCHUP_S + gapSec.
        const gapTicks = Math.max(0, liveTick - this.headTickF);
        const gapSec = gapTicks / tickHz;
        this.peakRate = 1.375 + (2 * gapSec) / RAMPCATCHUP_S;
        this.phase = 'rampcatchup';
        this.phaseElapsed = 0;
      }
    } else if (this.phase === 'rampcatchup') {
      this.phaseElapsed += realDt;
      const tau = Math.min(1, this.phaseElapsed / RAMPCATCHUP_S);
      const rate = this.rampRate(tau);
      this.headTickF += realDt * rate * tickHz;
      if (tau >= 1) {
        // Land exactly on live — the integral is analytically correct, but
        // accumulated float drift over many ticks could leave a sub-tick
        // residual.
        this.headTickF = liveTick;
        this.phase = 'idle';
        this.phaseElapsed = 0;
      }
    }

    // Never let head overshoot live — the ring would return null for a
    // future tick, and it'd be visually wrong anyway.
    if (this.headTickF > liveTick) this.headTickF = liveTick;

    this.lastHeadVel = realDt > 0 ? (this.headTickF - prevHead) / realDt : tickHz;
  }

  /** Rate curve for rampcatchup. τ ∈ [0, 1]. First half: smoothstep from
   *  SLOWMO_RATE up to peakRate. Second half: smoothstep from peakRate
   *  back down to 1.0. Zero slope at τ=0 / 0.5 / 1 keeps the tempo glide
   *  click-free across every boundary. */
  private rampRate(tau: number): number {
    if (tau < 0.5) {
      return SLOWMO_RATE + (this.peakRate - SLOWMO_RATE) * ss(tau * 2);
    }
    return this.peakRate - (this.peakRate - 1) * ss((tau - 0.5) * 2);
  }

  /** True when the render path should read from the buffer at headTick()
   *  rather than the live snapshot. */
  active(): boolean { return this.phase !== 'idle'; }

  /** Float tick the renderer should display. Integer part = curr snapshot,
   *  fractional part = interpolation alpha between prev and curr. */
  headTick(): number { return this.headTickF; }

  /** Playback rate relative to live sim (1 = normal, 0.25 = slowmo,
   *  >1 mid-rampcatchup). Feeds the audio low-pass cutoff map (clamped to
   *  bypass at ≥1×) and the renderer's visual timeScale. */
  playbackRate(): number {
    if (this.phase === 'idle') return 1;
    return this.lastHeadVel / CONFIG.sim.tickHz;
  }

  /** 0..1 desaturation amount: fades in over the first 120ms of slowmo,
   *  holds at 1 through slowmo, fades out across rampcatchup so by the
   *  time the head lands on live the scene reads in full color again. */
  desatAmount(): number {
    if (this.phase === 'slowmo') {
      return Math.min(1, this.phaseElapsed / 0.12);
    }
    if (this.phase === 'rampcatchup') {
      const tau = Math.min(1, this.phaseElapsed / RAMPCATCHUP_S);
      return 1 - ss(tau);
    }
    return 0;
  }

  /** 0..1 blur amount. Covers the whole KO event: rises in over the first
   *  120ms of slowmo, holds at 1 through the rest of slowmo and the rising
   *  half of rampcatchup (where motion speeds up), then smoothstep-fades to
   *  0 over the second half. The renderer multiplies this by a max-px
   *  constant and applies it to the canvas. */
  blurAmount(): number {
    if (this.phase === 'slowmo') {
      return Math.min(1, this.phaseElapsed / 0.12);
    }
    if (this.phase === 'rampcatchup') {
      const tau = Math.min(1, this.phaseElapsed / RAMPCATCHUP_S);
      if (tau < 0.5) return 1;
      return 1 - ss((tau - 0.5) * 2);
    }
    return 0;
  }

  reset(): void {
    this.phase = 'idle';
    this.phaseElapsed = 0;
    this.prevKnockedOut = false;
    this.prevRoundOver = false;
    this.lastHeadVel = CONFIG.sim.tickHz;
  }
}
